import { useState, useEffect } from 'react';
import axios from 'axios';
import { UserPlus, Trash2, Loader2, Mail, ShieldCheck, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { id: 'view', label: 'View', desc: 'Can see everything, read-only' },
  { id: 'comment', label: 'Comment', desc: 'Can view and leave comments' },
  { id: 'edit', label: 'Edit', desc: 'Can update items and upload files' },
  { id: 'admin', label: 'Admin', desc: 'Can edit, export, and see activity' },
];

export default function ShareDialog({ open, onClose }) {
  const token = localStorage.getItem('session_token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [shares, setShares] = useState([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('view');
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/readiness/shares`, auth);
      setShares(r.data || []);
    } catch { /* noop */ } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line

  const add = async () => {
    if (!email.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${BACKEND_URL}/api/readiness/shares`, { email: email.trim(), role }, auth);
      toast.success(`Shared with ${email.trim()}`);
      setEmail('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not share.');
    } finally { setAdding(false); }
  };

  const changeRole = async (share_id, newRole) => {
    try {
      await axios.put(`${BACKEND_URL}/api/readiness/shares/${share_id}`, { role: newRole }, auth);
      setShares((s) => s.map((x) => (x.share_id === share_id ? { ...x, role: newRole } : x)));
      toast.success('Role updated.');
    } catch { toast.error('Could not update role.'); }
  };

  const revoke = async (share_id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/readiness/shares/${share_id}`, auth);
      setShares((s) => s.filter((x) => x.share_id !== share_id));
      toast.success('Access revoked.');
    } catch { toast.error('Could not revoke access.'); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg rounded-3xl bg-white" data-testid="readiness-share-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#2C3A47] flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-[#4F86C6]" /> Share your checklist
          </DialogTitle>
          <DialogDescription className="sr-only">Invite family members and manage their access roles.</DialogDescription>
        </DialogHeader>

        <p className="text-sm text-[#8D8880] -mt-1">Invite family members and choose what they can do. They'll see it under "Shared with me".</p>

        <div className="flex flex-col sm:flex-row gap-2 mt-2">
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A8A29E]" />
            <Input data-testid="share-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="family@example.com" className="pl-9 rounded-full border-[#EAE7E2] h-11" />
          </div>
          <select data-testid="share-role-select" value={role} onChange={(e) => setRole(e.target.value)}
            className="h-11 rounded-full border border-[#EAE7E2] bg-white px-4 text-sm text-[#57534E]">
            {ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
          <Button data-testid="share-add-button" onClick={add} disabled={adding}
            className="rounded-full bg-[#4F86C6] hover:bg-[#3A6EA5] text-white h-11 px-5">
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Invite'}
          </Button>
        </div>
        <p className="text-xs text-[#9C948B]">{ROLES.find((r) => r.id === role)?.desc}</p>

        <div className="mt-3 space-y-2 max-h-72 overflow-y-auto">
          {loading ? (
            <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#4F86C6]" /></div>
          ) : shares.length === 0 ? (
            <p className="text-sm text-[#9C948B] text-center py-6">No one has access yet.</p>
          ) : shares.map((s) => (
            <div key={s.share_id} data-testid={`share-row-${s.share_id}`}
              className="flex items-center gap-3 bg-[#FAFBFC] border border-[#EEECE8] rounded-2xl px-4 py-3">
              <div className="w-9 h-9 rounded-full bg-[#E8F0F9] flex items-center justify-center text-[#3A6EA5] font-bold text-sm shrink-0">
                {(s.invited_name || s.invited_email || '?')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#2C3A47] truncate text-sm">{s.invited_name || s.invited_email}</p>
                <p className="text-xs text-[#8D8880] flex items-center gap-1">
                  {s.status === 'active'
                    ? <><ShieldCheck className="h-3 w-3 text-[#7FA98C]" /> Active</>
                    : <><Clock className="h-3 w-3 text-[#E9B949]" /> Pending sign-up</>}
                </p>
              </div>
              <select value={s.role} onChange={(e) => changeRole(s.share_id, e.target.value)}
                data-testid={`share-role-${s.share_id}`}
                className="h-9 rounded-full border border-[#EAE7E2] bg-white px-3 text-xs text-[#57534E]">
                {ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
              <button onClick={() => revoke(s.share_id)} data-testid={`share-revoke-${s.share_id}`}
                className="text-[#C0492E] hover:bg-[#FBE9E7] p-2 rounded-full"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
